import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { useMeQuery } from "@/api/auth/useMeQuery";
import { AnimatedOutlet } from "@/components/layout/AnimatedOutlet";
import LoadingScreen from "@/components/feedback/LoadingScreen";
import { VideoGenerationProvider } from "@/components/video/VideoGenerationProvider";
import { useAuthStore } from "@/stores/auth.store";
import { useWorkspaceStore } from "@/stores/workspace.store";

export const Route = createFileRoute("/_auth")({
  component: RouteComponent,
});

function RouteComponent() {
  const { isLoading, isError, data } = useMeQuery();
  const setToken = useAuthStore((s) => s.setToken);
  const setActiveWorkspaceId = useWorkspaceStore((s) => s.setActiveWorkspaceId);
  const setActiveProjectId = useWorkspaceStore((s) => s.setActiveProjectId);

  const currentWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);

  useEffect(() => {
    if (isError) {
      setToken(null);
    } else if (data) {
      // Only set workspace/project if not already set (initial load or account switch)
      const membership = data.memberships[0];
      const workspace = membership?.workspace;
      const hasValidWorkspace = currentWorkspaceId && data.memberships.some(
        (m) => m.workspace.id === currentWorkspaceId
      );

      if (!hasValidWorkspace) {
        if (workspace?.projects[0]) {
          setActiveWorkspaceId(workspace.id);
          setActiveProjectId(workspace.projects[0].id);
        } else {
          setActiveWorkspaceId(null);
          setActiveProjectId("");
        }
      }
    }
  }, [isError, setToken, data, setActiveWorkspaceId, setActiveProjectId, currentWorkspaceId]);

  const token = useAuthStore((s) => s.token);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isError || !token || !data) {
    return <Navigate to="/login" />;
  }

  if (data && !data.user.tosAcceptedAt) {
    return <Navigate to="/accept-tos" />;
  }

  return (
    <>
      <VideoGenerationProvider />
      <AnimatedOutlet />
    </>
  );
}
